// Modules
import decode from 'jwt-decode'

// Helpers
import { handleStorage, removeNonRequiredDataFromStorage } from 'helpers/system'

// Constants
import { APPLICATION_URL_PATH_PREFIX } from 'constants/environment'

// Helper: Authentication > Check if authentication failed
// NOTE: The REST endpoints answer 401, while GraphQL answers 200 with an UNAUTHENTICATED error
function checkIfAuthenticationFailed (error) {
	if (!error) return false
	const status = error.response?.status || error.networkError?.statusCode
	if (status === 401 || status === 403) return true
	return !!error.graphQLErrors?.some(graphQLError => graphQLError.extensions?.code === 'UNAUTHENTICATED')
}

// Helper: Authentication > Check if token is valid
function checkIfTokenIsValid () {
	const storedToken = getToken()
	if (!storedToken?.accessToken || !storedToken?.refreshToken) return false
	else {
		try {
			const decodedAccessToken = decode(storedToken.accessToken)
			if (typeof(decodedAccessToken?.exp) !== 'number') return false
			else {
				const remaningTokenTime = decodedAccessToken.exp * 1000 - new Date().getTime()
				if (remaningTokenTime < 0) return false
			}
		} catch (error) {
			return false
		}
	}
	return true
}

// Helper: Authentication > Get authorization header
function getAuthorizationHeader () {
	const token = getToken()?.accessToken
	if (!token) console.error('AIX | Authorization header error:', 'Missing token')
	return { Authorization: `Bearer ${token}` }
}

// Helper: Authentication > Get remaining token time
function getRemainingTokenTime (token) {
	const storedToken = token || getToken()
	const decodedAccessToken = decode(storedToken.accessToken)
	return decodedAccessToken.exp * 1000 - new Date().getTime()
}

// Helper: Authentication > Get team
function getTeam () {
	const team = handleStorage('get', 'team')
	return team ? Number(team) : null
}

// Helper: Authentication > Get team header
function getTeamHeader () {
	const team = getTeam()
	if (!team) console.error('AIX | Team header error:', 'Missing team id')
	return { 'x-team': team }
}

// Helper: Authentication > Get token
function getToken () {
	const token = handleStorage('get', 'token')
	return token ? JSON.parse(token) : null
}

// Helper: Authentication > Logout
function logout ({ redirectEvent = 'login' } = {}) {
	const storedRedirectURL = localStorage.getItem('redirectURL')
	removeNonRequiredDataFromStorage()
	window.dataLayer.push({ event: 'account-logout' })
	if (redirectEvent === 'logout') window.location.href = decodeURIComponent(storedRedirectURL)
	else {
		if (!!storedRedirectURL) localStorage.setItem('redirectURL', storedRedirectURL) // Sets it back to storage because storage is cleaned by an earlier function
		let url = window.location.origin
		if (!!APPLICATION_URL_PATH_PREFIX) url += APPLICATION_URL_PATH_PREFIX
		window.location.href = url
	}
}

// Helper: Authentication > Remove team
function removeTeam () {
	handleStorage('remove', 'team')
}

// Helper: Authentication > Remove token
function removeToken () {
	handleStorage('remove', 'token')
}

// Helper: Authentication > Store team
function storeTeam (teamId) {
	handleStorage('set', 'team', teamId)
}

// Helper: Authentication > Store token
function storeToken (token) {
	handleStorage('set', 'token', JSON.stringify(token))
}

// Export
export {
	checkIfAuthenticationFailed,
	checkIfTokenIsValid,
	getAuthorizationHeader,
	getRemainingTokenTime,
	getTeam,
	getTeamHeader,
	getToken,
	logout,
	removeTeam,
	removeToken,
	storeTeam,
	storeToken
}
